// src/pages/compliance/TermsOfService.tsx
import React, { useEffect } from "react";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import { FileText, Users, Shield, AlertTriangle, Gavel, CreditCard, Ban, Scale } from "lucide-react";
import Header from "../Home/Header";
import Footer from "../Home/Footer";

const TermsOfService: React.FC = () => {
    useEffect(() => {
        document.title = "Terms of Service - BidBro";
        window.scrollTo(0, 0);
    }, []);

    const sections = [
        {
            icon: Users,
            title: "User Accounts and Eligibility",
            content: [
                {
                    subtitle: "Account Requirements",
                    items: [
                        "You must be at least 18 years old to use our platform",
                        "You must provide accurate and complete information when creating an account",
                        "You are responsible for maintaining the security of your account credentials",
                        "You may only maintain one active account per person or business entity",
                        "Business accounts must be authorized by an official representative"
                    ]
                },
                {
                    subtitle: "Account Responsibilities",
                    items: [
                        "Keep your contact information current and accurate",
                        "Notify us immediately of any unauthorized account access",
                        "You are liable for all activities conducted through your account",
                        "Comply with all applicable laws and regulations in your jurisdiction",
                        "Maintain professional conduct in all platform interactions"
                    ]
                },
                {
                    subtitle: "Contractor Verification",
                    items: [
                        "Contractors must provide valid licensing and insurance documentation",
                        "Background checks may be required for platform participation",
                        "Professional credentials are subject to ongoing verification",
                        "False or misleading information may result in account termination",
                        "Additional documentation may be requested at any time"
                    ]
                }
            ]
        },
        {
            icon: FileText,
            title: "Platform Services",
            content: [
                {
                    subtitle: "Service Description",
                    items: [
                        "BidBro connects homeowners with contractors for home improvement projects",
                        "We facilitate the bidding process but are not party to actual contracts",
                        "Our platform provides communication tools and project management features",
                        "We offer dispute resolution assistance but do not guarantee outcomes",
                        "Services may be modified, suspended, or discontinued at any time"
                    ]
                },
                {
                    subtitle: "Bidding Process",
                    items: [
                        "Homeowners post project details and requirements",
                        "Qualified contractors submit contingent quotes based on provided information",
                        "Final pricing is subject to on-site inspection and agreement",
                        "Contractors may withdraw or modify bids before acceptance",
                        "Homeowners are not obligated to accept any submitted bids"
                    ]
                },
                {
                    subtitle: "Project Management",
                    items: [
                        "Communication between parties is facilitated through our platform",
                        "Project milestones and payments are tracked when using our services",
                        "Document sharing and photo uploads are available for project coordination",
                        "We provide tools but are not responsible for project completion",
                        "Actual work contracts are between homeowners and contractors directly"
                    ]
                }
            ]
        },
        {
            icon: Shield,
            title: "User Conduct and Prohibited Activities",
            content: [
                {
                    subtitle: "Acceptable Use",
                    items: [
                        "Use the platform only for legitimate home improvement projects",
                        "Provide truthful and accurate information in all communications",
                        "Respect other users and maintain professional communication",
                        "Comply with all local, state, and federal laws",
                        "Use platform features only for their intended purposes"
                    ]
                },
                {
                    subtitle: "Prohibited Activities",
                    items: [
                        "Impersonating other individuals or businesses",
                        "Posting false, misleading, or fraudulent information",
                        "Attempting to bypass platform fees or payment systems",
                        "Soliciting users to conduct business outside the platform to avoid fees",
                        "Harassing, threatening, or discriminating against other users"
                    ]
                },
                {
                    subtitle: "Content Restrictions",
                    items: [
                        "No illegal, harmful, or offensive content",
                        "No spam, promotional content unrelated to services offered",
                        "No copyrighted material without proper authorization",
                        "No personal information of third parties without consent",
                        "No malicious software, viruses, or harmful code"
                    ]
                }
            ]
        },
        {
            icon: CreditCard,
            title: "Fees and Payments",
            content: [
                {
                    subtitle: "Platform Fees",
                    items: [
                        "Service fees may apply to successful project connections",
                        "Payment processing fees are charged according to our fee schedule",
                        "Subscription fees for premium features are billed according to selected plan",
                        "All fees are non-refundable unless specifically stated otherwise",
                        "Fee structures may be modified with 30 days advance notice"
                    ]
                },
                {
                    subtitle: "Payment Processing",
                    items: [
                        "Payments are processed through secure third-party payment processors",
                        "You authorize us to charge your selected payment method",
                        "Failed payments may result in service suspension or termination",
                        "Disputed charges must be reported within 60 days of billing",
                        "Refunds, when applicable, will be processed within 5-10 business days"
                    ]
                },
                {
                    subtitle: "Project Payments",
                    items: [
                        "Direct payments between homeowners and contractors are not our responsibility",
                        "Optional escrow services may be available for qualifying projects",
                        "Payment disputes between parties should be resolved directly",
                        "We may assist with dispute resolution but are not liable for outcomes",
                        "Tax responsibilities for payments rest with the receiving party"
                    ]
                }
            ]
        },
        {
            icon: Gavel,
            title: "Intellectual Property",
            content: [
                {
                    subtitle: "Platform Content",
                    items: [
                        "BidBro owns all rights to the platform, including software and design",
                        "Our trademarks, logos, and branding are protected intellectual property",
                        "You may not copy, modify, or distribute our proprietary content",
                        "Limited license granted for personal use of platform features only",
                        "Unauthorized use of our intellectual property is prohibited"
                    ]
                },
                {
                    subtitle: "User Content",
                    items: [
                        "You retain ownership of content you submit to the platform",
                        "You grant us license to use your content for platform operations",
                        "You represent that you have rights to all content you submit",
                        "We may remove content that violates these terms or applicable law",
                        "You are responsible for protecting your own intellectual property"
                    ]
                },
                {
                    subtitle: "Copyright Compliance",
                    items: [
                        "We comply with the Digital Millennium Copyright Act (DMCA)",
                        "Copyright infringement notices should be sent to our designated agent",
                        "Repeat copyright violators may have their accounts terminated",
                        "Counter-notifications may be submitted for disputed takedowns",
                        "We reserve the right to remove allegedly infringing content"
                    ]
                }
            ]
        },
        {
            icon: AlertTriangle,
            title: "Disclaimers and Limitations",
            content: [
                {
                    subtitle: "Service Disclaimers",
                    items: [
                        "Platform services are provided 'as is' without warranties of any kind",
                        "We do not guarantee continuous, uninterrupted access to our services",
                        "We are not responsible for the quality of work performed by contractors",
                        "We do not warrant that our platform is error-free or secure",
                        "Third-party integrations are subject to their own terms and availability"
                    ]
                },
                {
                    subtitle: "Contractor Relationships",
                    items: [
                        "We do not employ contractors and are not responsible for their actions",
                        "Contractor licensing and insurance verification does not guarantee performance",
                        "Work quality, timeliness, and contractor reliability are not our responsibility",
                        "Disputes between homeowners and contractors must be resolved directly",
                        "We may assist with dispute resolution but make no guarantees"
                    ]
                },
                {
                    subtitle: "Limitation of Liability",
                    items: [
                        "Our liability is limited to the fees paid for platform services",
                        "We are not liable for indirect, incidental, or consequential damages",
                        "Property damage, personal injury, or project delays are not our responsibility",
                        "Force majeure events excuse performance of obligations",
                        "Some jurisdictions may not allow liability limitations"
                    ]
                }
            ]
        },
        {
            icon: Ban,
            title: "Termination and Suspension",
            content: [
                {
                    subtitle: "Account Termination",
                    items: [
                        "You may terminate your account at any time through account settings",
                        "We may terminate accounts for violations of these terms",
                        "Termination may be immediate for serious violations or illegal activity",
                        "Suspended accounts may be reactivated upon resolution of issues",
                        "Terminated accounts cannot be reactivated without our approval"
                    ]
                },
                {
                    subtitle: "Effects of Termination",
                    items: [
                        "Access to platform services will be immediately revoked",
                        "Outstanding fees and obligations remain due after termination",
                        "User content may be deleted but some records may be retained",
                        "Active projects may continue through completion with limited platform access",
                        "Termination does not affect rights and obligations that survive termination"
                    ]
                },
                {
                    subtitle: "Data Retention",
                    items: [
                        "Some user data may be retained for legal and business purposes",
                        "Financial records are retained as required by law",
                        "Communication records may be preserved for dispute resolution",
                        "Personal data deletion requests will be honored where legally permitted",
                        "Anonymized data may be retained indefinitely for analytics"
                    ]
                }
            ]
        },
        {
            icon: Scale,
            title: "Legal and Dispute Resolution",
            content: [
                {
                    subtitle: "Governing Law",
                    items: [
                        "These terms are governed by the laws of the State of Delaware",
                        "Federal law applies where specified or where state law is preempted",
                        "International users are subject to applicable local laws",
                        "Conflicts of law principles do not apply",
                        "Any invalid provisions do not affect the validity of remaining terms"
                    ]
                },
                {
                    subtitle: "Dispute Resolution",
                    items: [
                        "Disputes should first be addressed through our customer support",
                        "Mediation may be required before pursuing legal action",
                        "Arbitration may be required for certain types of disputes",
                        "Class action lawsuits are waived where legally permissible",
                        "Venue for legal proceedings is Delaware"
                    ]
                },
                {
                    subtitle: "Indemnification",
                    items: [
                        "Users agree to indemnify BidBro against claims arising from their use",
                        "Contractors indemnify against claims related to their work or services",
                        "Users are responsible for their own actions and content",
                        "We will provide notice of claims and cooperate in defense where appropriate",
                        "Indemnification survives termination of these terms"
                    ]
                }
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            <Header isFullWidth={true} />

            <main style={{marginTop: '75px', paddingTop: '50px'}}>
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={10} xl={8}>
                            {/* Header Section */}
                            <div className="text-center mb-5">
                                <div className="d-flex align-items-center justify-content-center mb-3">
                                    <FileText className="text-primary me-2" size={32} />
                                    <h1 className="display-4 fw-bold mb-0">Terms of Service</h1>
                                </div>
                                <p className="text-muted fs-5 mb-4">
                                    Please read these terms carefully before using our platform. By using BidBro, you agree to these terms.
                                </p>
                                <div className="bg-light rounded p-3 mb-4">
                                    <p className="mb-2">
                                        <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </p>
                                    <p className="mb-0 text-muted">
                                        <strong>Effective Date:</strong> These terms are effective immediately for new users and will be effective 30 days after posting for existing users.
                                    </p>
                                </div>
                            </div>

                            {/* Introduction */}
                            <Card className="shadow-sm mb-5">
                                <CardBody className="p-4">
                                    <h2 className="h4 fw-bold mb-3">Agreement to Terms</h2>
                                    <p className="text-muted mb-3">
                                        These Terms of Service ("Terms") constitute a legally binding agreement between you and BidBro, Inc ("BidBro," "we," "us," or "our")
                                        regarding your use of our website, mobile applications, and related services (collectively, the "Platform").
                                    </p>
                                    <p className="text-muted mb-3">
                                        By accessing or using our Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms.
                                        If you do not agree to these Terms, you may not access or use our Platform.
                                    </p>
                                    <p className="text-muted mb-3">
                                        We may modify these Terms at any time by posting the revised terms on our Platform. Your continued use of the Platform
                                        after such modifications constitutes your acceptance of the modified Terms.
                                    </p>
                                    <div className="alert alert-warning">
                                        <strong>Important:</strong> These Terms include provisions that limit our liability and require individual arbitration
                                        of disputes rather than class actions. Please review these provisions carefully.
                                    </div>
                                </CardBody>
                            </Card>

                            {/* Terms Sections */}
                            {sections.map((section, index) => (
                                <Card key={index} className="shadow-sm mb-4">
                                    <CardBody className="p-4">
                                        <div className="d-flex align-items-center mb-4">
                                            <section.icon className="text-primary me-3" size={24} />
                                            <h2 className="h4 fw-bold mb-0">{section.title}</h2>
                                        </div>

                                        {section.content.map((subsection, subIndex) => (
                                            <div key={subIndex} className="mb-4">
                                                <h3 className="h5 fw-semibold mb-3 text-primary">{subsection.subtitle}</h3>
                                                <ul className="list-unstyled">
                                                    {subsection.items.map((item, itemIndex) => (
                                                        <li key={itemIndex} className="mb-2 d-flex">
                                                            <span className="text-primary me-2">•</span>
                                                            <span className="text-muted">{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </CardBody>
                                </Card>
                            ))}

                            {/* Additional Terms and Contact */}
                            <Card className="shadow-sm mb-5">
                                <CardBody className="p-4">
                                    <h2 className="h4 fw-bold mb-4">Additional Terms and Policies</h2>

                                    <Row>
                                        <Col md={6}>
                                            <h3 className="h5 fw-semibold mb-3">Related Policies</h3>
                                            <ul className="list-unstyled">
                                                <li className="mb-2">
                                                    <a href="/privacy-policy" className="text-decoration-none">Privacy Policy</a> - How we handle your personal information
                                                </li>
                                                {/*
                                                <li className="mb-2">
                                                    <a href="/cookie-policy" className="text-decoration-none">Cookie Policy</a> - Our use of cookies and tracking
                                                </li>
                                                <li className="mb-2">
                                                    <a href="/community-guidelines" className="text-decoration-none">Community Guidelines</a> - Standards for platform behavior
                                                </li>
                                                <li className="mb-4">
                                                    <a href="/contractor-agreement" className="text-decoration-none">Contractor Agreement</a> - Additional terms for service providers
                                                </li>
                                                */}
                                            </ul>
                                        </Col>
                                        <Col md={6}>
                                            <h3 className="h5 fw-semibold mb-3">Contact Information</h3>
                                            <p className="text-muted mb-2">
                                                <strong>General Questions:</strong><br />
                                                <a href="mailto:support@bidbro.com" className="text-decoration-none">support@bidbro.com</a>
                                            </p>
                                            <p className="text-muted mb-2">
                                                <strong>Legal Notices:</strong><br />
                                                <a href="mailto:legal@bidbro.com" className="text-decoration-none">legal@bidbro.com</a>
                                            </p>
                                            {/*
                                            <p className="text-muted mb-2">
                                                <strong>Phone:</strong> 1-800-BIDBRO (1-800-243-2761)
                                            </p>
                                            */}
                                        </Col>
                                    </Row>

                                    <hr className="my-4" />

                                    <h3 className="h5 fw-semibold mb-3">Mailing Address</h3>
                                    <p className="text-muted mb-4">
                                        BidBro, Inc<br />
                                        16192 Coastal Highway<br />
                                        Lewes, DE 19958
                                    </p>

                                    <div className="alert alert-info">
                                        <h4 className="alert-heading h5">Questions About These Terms?</h4>
                                        <p className="mb-2">
                                            If you have questions about these Terms of Service, please contact us using the information above.
                                            We're committed to ensuring you understand your rights and obligations.
                                        </p>
                                        <p className="mb-0">
                                            For immediate assistance, use our live chat feature or call our support line during business hours.
                                        </p>
                                    </div>
                                </CardBody>
                            </Card>

                            {/* Acknowledgment */}
                            <Card className="shadow-sm bg-light mb-5">
                                <CardBody className="p-4 text-center">
                                    <h3 className="h5 fw-bold mb-3">Acknowledgment</h3>
                                    <p className="text-muted mb-3">
                                        By using BidBro's platform, you acknowledge that you have read these Terms of Service,
                                        understand them, and agree to be bound by their terms and conditions.
                                    </p>
                                    <p className="text-muted mb-0">
                                        These Terms, together with our Privacy Policy and any other legal notices published by us on the Platform,
                                        constitute the entire agreement between you and BidBro concerning your use of the Platform.
                                    </p>
                                </CardBody>
                            </Card>

                            {/* Legal Disclaimer */}
                            <div className="text-center text-muted small mb-5">
                                <p className="mb-2">
                                    This Terms of Service agreement was last updated on {new Date().toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}.
                                    Previous versions are available upon request.
                                </p>
                                <p className="mb-0">
                                    © {new Date().getFullYear()} BidBro, Inc. All rights reserved.
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </main>

            <Footer />
        </div>
    );
};

export default TermsOfService;